import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import {
  DvCheckboxElement,
  DvDatePickerLegacyElement,
  DvDateTimePickerElement,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvNumberboxElement,
  DvSlideToggleElement,
  DvSpaceElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { Entity } from '@shared/constants/entity';
import { FindTagsDto } from '@pages/settings-page/hardware-devices-settings/components/find-tags/find-tags-tabs/find-tags-information-tab/interfaces';

import { FindTagWithIcon } from './interfaces';
import {
  HardwareDeviceStatus,
  HardwareDeviceStatusIcon,
} from '@shared/constants/hardware-device-status';
import { dvNumberMaskPresets } from '@devinco/ui/dds';
import { activeRecordFilter } from '@shared/constants/active-record-filter';

@Injectable()
export class AssetsInformationTabService {
  private readonly api = inject(RestApiService);

  readonly informationElements = [
    new DvTextboxElement({
      colspan: 12,
      key: 'Description',
      label: 'Asset.EquipmentDescription',
      validators: [Validators.required],
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'FindID',
      label: 'Asset.SerialNo',
      labelKeys: ['Serial'],
      valueKey: 'FindID',
      iconKey: 'IconName',
      iconColorKey: 'IconColor',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Find,
          fields: ['FindID', 'Serial', 'Active'],
          filterModel: {
            ConnectedTo: {
              filterType: 'text',
              type: 'blank',
            },
            ...activeRecordFilter,
          },
        };
        const { api, control } = params;
        api.setIsLoading(true);
        this.api.getData(request).subscribe(res => {
          const mappedData: FindTagWithIcon[] = (res.data as FindTagsDto[]).map(
            findItem => this.getFindItemWithIcon(findItem)
          );

          api.setIsLoading(false);
          api.setItems(mappedData);
          control?.markAsTouched();
        });
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'UserID',
      label: 'Asset.Owner',
      labelKeys: ['UserNameDescription'],
      valueKey: 'UserID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.AssetUser,
          fields: ['UserID', 'UserNameDescription'],
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'AssetTypeID',
      label: 'Asset.EquipmentType',
      labelKeys: ['AssetTypeDescription'],
      valueKey: 'AssetTypeID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.AssetType,
          fields: ['AssetTypeID', 'AssetTypeDescription'],
          filterModel: this.getEquipmentTypeDropdownFilterModel(),
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvCheckboxElement({
      label: ' ',
      colspan: 6,
      checkboxLabel: 'Asset.Private',
      key: 'Personal',
      infoIconText: 'Asset.PrivateCheckboxInfo',
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'DepartmentID',
      label: 'Asset.Department',
      labelKeys: ['DepartmentDescription'],
      valueKey: 'DepartmentID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      isMultipleSelectionEnabled: true,
      closeOnSelect: false,
      key: 'VisibleToDepartment',
      label: 'Asset.VisibleToDepartment',
      labelKeys: ['DepartmentDescription'],
      valueKey: 'DepartmentID',
      isSelectAllEnabled: true,
      isCheckboxEnabled: true,
      ready: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvTextboxElement({
      colspan: 12,
      key: 'Comment',
      label: 'Asset.Comment',
    }),
    new DvTextboxElement({
      colspan: 12,
      key: 'AssetSerial',
      label: 'Asset.AssetSerial',
    }),
    new DvTextboxElement({
      colspan: 12,
      key: 'ShelfLocation',
      label: 'Asset.PositionOnShelf',
    }),
    new DvDatePickerLegacyElement({
      colspan: 6,
      key: 'PurchaseDate',
      label: 'Asset.PurchasedDate',
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvDatePickerLegacyElement({
      colspan: 6,
      key: 'LastInspection',
      label: 'Asset.LastControlService',
    }),
    new DvDatePickerLegacyElement({
      colspan: 6,
      key: 'NextInspection',
      label: 'Asset.NextControlService',
    }),
    new DvCheckboxElement({
      colspan: 6,
      checkboxLabel: 'Asset.TempOutOfOrder',
      key: 'TemporaryInactive',
      infoIconText: 'Asset.TempOutOfOrderInfo',
    }),
    new DvTextboxElement({
      key: 'FindActive',
      hidden: true,
    }),
  ];

  readonly notificationElements = [
    new DvCheckboxElement({
      colspan: 12,
      checkboxLabel: 'Asset.NotifyNotInCar',
      key: 'NotificationLocationLeaving',
      infoIconText: 'Asset.NoOwnerInfoText',
    }),
    new DvNumberboxElement({
      colspan: 12,
      key: 'NotificationInactiveDays',
      label: 'Asset.NotifyInactiveDays',
    }),
    new DvSlideToggleElement({
      colspan: 12,
      key: 'NotificationLostTime',
      label: 'Asset.ReportMissingEquipment',
    }),
  ];

  readonly lastUpdatesElements = (
    setColorFn: () => string
  ): DvDynamicFormElement[] => [
    new DvDateTimePickerElement({
      colspan: 6,
      key: 'LastActivityTime',
      label: 'Asset.LastActivityTime',
      disabled: true,
    }),
    new DvDateTimePickerElement({
      colspan: 6,
      key: 'LastMoved',
      label: 'Asset.LastMoved',
      disabled: true,
    }),
    new DvTextboxElement({
      backgroundColor: setColorFn(),
      colspan: 6,
      key: 'Voltage',
      label: 'Asset.Voltage',
      disabled: true,
    }),
    new DvNumberboxElement({
      colspan: 6,
      key: 'Temperature',
      label: 'Asset.Temperature',
      disabled: true,
      fieldMask: dvNumberMaskPresets.NegativeFormattedDecimalNumber,
    }),
    new DvTextboxElement({
      key: 'Department',
      hidden: true,
    }),
  ];

  getFindItemWithIcon(findItem: Partial<FindTagsDto>): FindTagWithIcon {
    return {
      ...findItem,
      IconName:
        findItem.Active === HardwareDeviceStatus.Active
          ? HardwareDeviceStatusIcon.Active
          : HardwareDeviceStatusIcon.Inactive,
      IconColor:
        findItem.Active === HardwareDeviceStatus.Active ? 'green' : 'red',
    };
  }

  private getEquipmentTypeDropdownFilterModel(): FilterModelWithKeys {
    return {
      Passive: {
        filterType: 'number',
        type: 'equals',
        filter: 0,
      },
    };
  }
}
